import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { sampleConfig } from "../../../../../data/live-charts/config";
import type { QuestCandleData } from "../../../../../context/quest/api";
import type { Page, PageParams } from "./useHistorical";

type SampleCacheKey = { sampleTime: keyof typeof sampleConfig; key: readonly unknown[] };

export const updateCloses = (
  client: QueryClient,
  cacheKeys: SampleCacheKey[],
  data: QuestCandleData[],
  sampleTime: keyof typeof sampleConfig,
) => {
  for (const cKey of cacheKeys) {
    if (cKey.sampleTime === sampleTime) continue; // This is the one we just merged
    client.setQueryData<InfiniteData<Page, PageParams>>(cKey.key, (x) => {
      if (!x) return undefined;
      const cached = x.pages[0].data;
      const merged = cached.map((candle, idx) => {
        // The first candle is the live one.
        if (idx === 0) {
          const updated = [...candle];
          updated[2] = data[0][2]; // Update the close price with the live candle close price
          return updated as QuestCandleData;
        }
        return candle;
      });
      const pages = [{ data: merged, ended: false }, ...x.pages.slice(1)];
      const pageParams = [{ ...x.pageParams[0], to: merged[0][0] }, ...x.pageParams.slice(1)];
      return { ...x, pages, pageParams };
    });
  }
};

// For dev use, isn't actually used anywhere currently, but can be used to check if the cached closes from different sample times are equal to the live close price. If they are not equal, it means the close isn't updated across all sample times, which can cause the price to jump around when switching between sample times. This is only an issue when the user's connection is a bit slow, and can be tested by throttling the network in dev tools.
export const checkCandleCloseEquality = (client: QueryClient, cacheKeys: SampleCacheKey[], data: QuestCandleData[]) => {
  const cachedCloses: Record<string, number | undefined> = {};
  for (const cKey of cacheKeys) {
    if (!cKey.sampleTime) continue;
    const cached = client.getQueryData<InfiniteData<Page, PageParams>>(cKey.key);
    if (!cached) continue;

    const cachedData = cached.pages[0].data;
    const cachedClose = cachedData[0]?.[2];
    if (cachedClose === undefined) continue;
    cachedCloses[cKey.sampleTime] = cachedClose;
  }

  const closesEqual = Object.values(cachedCloses).every((close) => close === data[0][2]);
  if (closesEqual) {
    console.log("Cached closes from sample times", cachedCloses, "are closes equal", closesEqual);
  } else {
    console.warn("Cached closes from sample times", cachedCloses, "are not equal to live close", data[0][2]);
  }
};
